/**
 * ConditionalPatternStep Component - For CONDITIONAL pattern configuration
 * Used when user selected "Sometimes data is missing, hide or show different text" → "Just hide it completely"
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle2, ArrowRight, AlertTriangle, EyeOff } from 'lucide-react'
import { WizardState } from '../../../../types/macros'
import DataKeyInput from './shared/DataKeyInput'

export interface ConditionalPatternStepProps {
  wizardState: WizardState
  onUpdateState: (updates: Partial<WizardState>) => void
  onBack: () => void
}

const ConditionalPatternStep: React.FC<ConditionalPatternStepProps> = ({
  wizardState,
  onUpdateState,
  onBack
}) => {
  const [localState, setLocalState] = useState({
    dataKey: wizardState.patternData.conditional?.dataKey || '',
    conditionalContent: wizardState.patternData.conditional?.conditionalContent || '',
    description: wizardState.patternData.conditional?.description || ''
  })

  // Update parent state when local state changes
  React.useEffect(() => {
    onUpdateState({
      patternData: {
        ...wizardState.patternData,
        conditional: {
          dataKey: localState.dataKey,
          conditionalContent: localState.conditionalContent,
          description: localState.description
        }
      }
    })
  }, [localState, onUpdateState, wizardState.patternData])

  // Grammar validation - check for potential awkward phrasing
  const grammarWarnings = React.useMemo(() => {
    const warnings = []
    const content = localState.conditionalContent.toLowerCase()
    
    if (content.includes('your ') && !localState.dataKey.toLowerCase().includes('name')) {
      warnings.push("Consider if 'your' makes sense when the name might be missing")
    }
    
    if (content.includes(' a ') || content.includes(' an ') || content.includes(' the ')) {
      warnings.push("Articles like 'a/an/the' might create awkward phrasing when content disappears")
    }
    
    if (content.trim().endsWith(',') || content.trim().endsWith('.')) {
      warnings.push("Punctuation at the end might look odd when this content is hidden")
    }
    
    return warnings
  }, [localState.conditionalContent, localState.dataKey])

  // Content suggestions based on data key
  const contentSuggestions = React.useMemo(() => {
    const key = localState.dataKey.toLowerCase()
    
    if (key.includes('name') || key.includes('first')) {
      return [
        `Welcome back, {{${localState.dataKey}}}!`,
        `Hi {{${localState.dataKey}}},`,
        `{{${localState.dataKey}}}, thanks for returning!`
      ]
    }
    if (key.includes('tier') || key.includes('level')) {
      return [
        `{{${localState.dataKey}}} Member Exclusive:`,
        `As a {{${localState.dataKey}}} member,`,
        `{{${localState.dataKey}}} benefits inside:`
      ]
    }
    if (key.includes('points') || key.includes('balance')) {
      return [
        `You have {{${localState.dataKey}}} points available.`,
        `Your balance: {{${localState.dataKey}}}`,
        `{{${localState.dataKey}}} points ready to use!`
      ]
    }
    
    return [
      `Welcome back, {{${localState.dataKey}}}!`,
      `Hi {{${localState.dataKey}}},`,
      `{{${localState.dataKey}}} exclusive offer:`
    ]
  }, [localState.dataKey])

  // Validation
  const isValid = React.useMemo(() => {
    return localState.dataKey.trim() !== '' && 
           /^[a-zA-Z][a-zA-Z0-9_]*$/.test(localState.dataKey) &&
           localState.conditionalContent.trim() !== ''
  }, [localState.dataKey, localState.conditionalContent])

  const handleContentSuggestion = (suggestion: string) => {
    setLocalState(prev => ({ ...prev, conditionalContent: suggestion }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full mb-4">
          <EyeOff className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white mb-2">
          Conditional Pattern Setup
        </h2>
        <p className="text-[#6E6D7A] dark:text-gray-400 max-w-md mx-auto">
          Perfect for clean messaging! The content will only appear when data is available, keeping your messages elegant.
        </p>
      </div>

      {/* Pattern Confirmation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-1">
              You Selected: Conditional Pattern (Hide When Missing)
            </h3>
            <p className="text-sm text-purple-700 dark:text-purple-400">
              This pattern shows content only when data exists. When data is missing, 
              the entire section disappears cleanly - no awkward gaps or empty spaces.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Configuration Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white mb-4">
          Configuration
        </h3>

        <div className="space-y-6">
          {/* Data Key Input */}
          <DataKeyInput
            value={localState.dataKey}
            onChange={(value) => setLocalState(prev => ({ ...prev, dataKey: value }))}
            platform={wizardState.platform}
            label="Data Key"
            placeholder="e.g., first_name, loyalty_tier"
            helpText="The data field that determines whether to show the content"
            required={true}
          />

          {/* Conditional Content Input */}
          <div>
            <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white mb-2">
              Content to Show (When Data Exists) <span className="text-red-500">*</span>
            </label>
            <textarea
              value={localState.conditionalContent}
              onChange={(e) => setLocalState(prev => ({ ...prev, conditionalContent: e.target.value }))}
              placeholder="e.g., Welcome back, {{first_name}}!"
              rows={3}
              className="w-full p-3 border-2 border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215] rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              This content will only appear when the data key has a value. Use the macro syntax {'{dataKey}'} within your content.
            </p>

            {/* Content Suggestions */}
            {localState.dataKey && contentSuggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3"
              >
                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  💡 Content suggestions:
                </div>
                <div className="space-y-2">
                  {contentSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleContentSuggestion(suggestion)}
                      className="block w-full text-left px-3 py-2 text-xs bg-gray-50 hover:bg-[#FE4215]/10 text-gray-700 hover:text-[#FE4215] rounded border border-gray-200 hover:border-[#FE4215] transition-all"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Grammar Warnings */}
            {grammarWarnings.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                      Grammar Check:
                    </div>
                    <ul className="space-y-1">
                      {grammarWarnings.map((warning, index) => (
                        <li key={index} className="text-xs text-amber-700 dark:text-amber-400">
                          • {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white mb-2">
              Description (Optional)
            </label>
            <textarea
              value={localState.description}
              onChange={(e) => setLocalState(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of what this macro does..."
              rows={3}
              className="w-full p-3 border-2 border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215] rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
            />
          </div>
        </div>
      </motion.div>

      {/* How It Works */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4"
      >
        <h3 className="font-semibold text-violet-800 dark:text-violet-300 mb-2">
          How Conditional Patterns Work
        </h3>
        <div className="space-y-2 text-sm text-violet-700 dark:text-violet-400">
          <p>• When data exists: Shows your complete content with data inserted</p>
          <p>• When data is missing: The entire content block disappears</p>
          <p>• Messages remain clean and grammatically correct in both cases</p>
          <p>• Perfect for optional greetings and personalized sections</p>
        </div>
      </motion.div>

      {/* Example Scenarios */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4"
      >
        <h4 className="font-semibold text-[#1A2B49] dark:text-white mb-3">
          Example Use Cases
        </h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="font-medium text-gray-900 dark:text-gray-300">✅ Great for:</div>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Personalized greetings</li>
              <li>• Loyalty tier messaging</li>
              <li>• Optional promotional content</li>
              <li>• Conditional rewards display</li>
            </ul>
          </div>
          <div className="space-y-2">
            <div className="font-medium text-gray-900 dark:text-gray-300">💡 Best Practices:</div>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Keep messages self-contained</li>
              <li>• Avoid dangling punctuation</li>
              <li>• Test with and without data</li>
              <li>• Consider message flow</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#6E6D7A] hover:text-[#1A2B49] dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          ← Back to pattern selection
        </button>

        {isValid && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-sm text-green-600"
          >
            <CheckCircle2 className="w-4 h-4" />
            Ready to preview
            <ArrowRight className="w-4 h-4" />
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default ConditionalPatternStep